import { toast } from 'sonner'
import { paths } from '@/routes/paths'
import { useState, useEffect } from 'react'
import { fError } from '@/utils/format-error'
import { useBoolean } from '@/hooks/use-boolean'
import { useRouter } from '@/routes/hooks/use-router'
import axiosInstance, { endpoints } from '@/lib/axios'
import CustomPagination from '@/components/pagination/custom-pagination'
import ConfirmDialog from '@/components/share-components/confirm-dialog'
import LoadingSection from '@/components/loading-indicator/loading-section'

import type {
  TrendSound,
  TrendSoundFilters,
  TrendSoundListResponse,
  TrendSoundStatus,
} from '../types'

import TrendSoundTable from '../trend-sound-table'
import TrendSoundFilter from '../trend-sound-filter'

const PAGE_SIZE = 10

export default function TrendSoundView() {
  const router = useRouter()
  const loading = useBoolean(true)
  const [trendSounds, setTrendSounds] = useState<TrendSound[]>([])
  const [total, setTotal] = useState(0)
  const [filters, setFilters] = useState<TrendSoundFilters>({
    page: 1,
    limit: PAGE_SIZE,
    search: '',
    type: '',
    status: '',
    orderBy: 'createdAt:desc',
  })

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    trendSound: TrendSound | null
  }>({
    open: false,
    trendSound: null,
  })

  // Loading state for delete operation
  const [deleteLoading, setDeleteLoading] = useState(false)

  const fetchTrendSounds = async () => {
    try {
      loading.onTrue()

      const params: Record<string, string | number | undefined> = {
        page: filters.page,
        limit: filters.limit,
        orderBy: filters.orderBy,
      }

      if (filters.status) {
        params.status = filters.status
      }
      if (filters.type) {
        params.type = filters.type
      }

      const { data } = await axiosInstance.get<TrendSoundListResponse>(
        endpoints.trendSounds.list,
        {
          params,
        },
      )

      setTrendSounds(data.data)
      setTotal(data.total)
    } catch (error) {
      toast.error(fError(error))
    } finally {
      loading.onFalse()
    }
  }

  useEffect(() => {
    fetchTrendSounds()
  }, [filters])

  const handleFiltersChange = (newFilters: TrendSoundFilters) => {
    setFilters(newFilters)
  }

  const handleAddClick = () => {
    router.push(paths.trendSoundCreate)
  }

  const handleEdit = (trendSound: TrendSound) => {
    router.push(paths.trendSoundEdit(trendSound.id))
  }

  const handleDelete = (trendSound: TrendSound) => {
    setConfirmDialog({
      open: true,
      trendSound,
    })
  }

  const handleConfirmDelete = async () => {
    if (!confirmDialog.trendSound || deleteLoading) return

    try {
      setDeleteLoading(true)

      await axiosInstance.delete(
        endpoints.trendSounds.delete(confirmDialog.trendSound.id),
      )
      toast.success('ลบเสียงเทรนด์สำเร็จ')

      // Refresh the list
      fetchTrendSounds()
    } catch (error) {
      toast.error(fError(error))
    } finally {
      setDeleteLoading(false)
    }
  }

  const handlePageChange = (page: number) => {
    setFilters({
      ...filters,
      page,
    })
  }

  const handleStatusChange = async (
    trendSound: TrendSound,
    status: TrendSoundStatus,
  ) => {
    try {
      await axiosInstance.put(endpoints.trendSounds.update(trendSound.id), {
        status,
      })
      toast.success('เปลี่ยนสถานะสำเร็จ')
      fetchTrendSounds()
    } catch (error) {
      toast.error(fError(error))
    }
  }

  return (
    <div className="w-full">
      <LoadingSection loading={loading.value}>
        <div className="space-y-6">
          {/* Filter Section */}
          <TrendSoundFilter
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onAddClick={handleAddClick}
          />

          {/* Table Section */}
          <TrendSoundTable
            trendSounds={trendSounds}
            loading={loading.value}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
          />

          {/* Pagination */}
          {Math.ceil(total / PAGE_SIZE) > 1 && (
            <CustomPagination
              currentPage={filters.page}
              totalPages={Math.ceil(total / PAGE_SIZE)}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </LoadingSection>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ open, trendSound: null })}
        title="ยืนยันการลบ"
        description={`คุณต้องการลบเสียงเทรนด์ "${confirmDialog.trendSound?.title}" หรือไม่? การดำเนินการนี้ไม่สามารถยกเลิกได้`}
        confirmText="ลบ"
        cancelText="ยกเลิก"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />
    </div>
  )
}
